import React from 'react'

import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Form from 'react-bootstrap/Form'
import ProgressBar from 'react-bootstrap/ProgressBar'
import Spinner from 'react-bootstrap/Spinner'

export const isYes = (value) => value === 'Yes'
export const toYesNo = (checked) => (checked ? 'Yes' : 'No')

export function WizardFieldGroup({
  label,
  hint,
  children,
  error,
  className = '',
}) {
  return (
    <Form.Group className={`fundraiser-wizard__field ${className}`.trim()}>
      {label && (
        <Form.Label className="fundraiser-wizard__label">
          {label}
          {hint && (
            <span className="fundraiser-wizard__label-hint">{hint}</span>
          )}
        </Form.Label>
      )}
      {children}
      {error && <div className="error small mt-1">{error}</div>}
    </Form.Group>
  )
}

export function RadioPillGroup({
  name,
  value,
  options,
  onChange,
  className = '',
}) {
  return (
    <div className={`fundraiser-wizard__pill-group ${className}`.trim()}>
      {options.map((option) => {
        const optionValue = typeof option === 'string' ? option : option.value
        const optionLabel = typeof option === 'string' ? option : option.label

        return (
          <label
            key={optionValue}
            className={`fundraiser-wizard__pill${
              value === optionValue ? ' fundraiser-wizard__pill--selected' : ''
            }`}
          >
            <input
              type="radio"
              name={name}
              value={optionValue}
              checked={value === optionValue}
              onChange={() => onChange(optionValue)}
            />
            {optionLabel}
          </label>
        )
      })}
    </div>
  )
}

export function CheckCardGrid({ options, selectedValues, onToggle }) {
  return (
    <div className="fundraiser-wizard__check-grid">
      {options.map((option) => {
        const isChecked = selectedValues.includes(option)
        return (
          <label
            key={option}
            className={`fundraiser-wizard__check-card${
              isChecked ? ' fundraiser-wizard__check-card--checked' : ''
            }`}
          >
            <input
              type="checkbox"
              checked={isChecked}
              onChange={() => onToggle(option)}
            />
            {option}
          </label>
        )
      })}
    </div>
  )
}

export function ToggleRow({
  id,
  label,
  description,
  checked,
  onChange,
  disabled = false,
  className = '',
}) {
  return (
    <div className={`fundraiser-wizard__toggle-row ${className}`.trim()}>
      <div>
        <p className="fundraiser-wizard__toggle-label">{label}</p>
        {description && (
          <span className="fundraiser-wizard__toggle-desc">{description}</span>
        )}
      </div>
      <Form.Check
        type="switch"
        id={id}
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        aria-label={label}
        className="fundraiser-wizard__toggle-switch mb-0"
      />
    </div>
  )
}

export function SectionCard({ title, badge, children, className = '' }) {
  return (
    <Card
      body
      className={`fundraiser-wizard__section-card card--sm card-with-border ${className}`.trim()}
    >
      {(title || badge) && (
        <div className="fundraiser-wizard__section-card-header">
          {title && <h4 className="card-title card-title-xs mb-0">{title}</h4>}
          {badge}
        </div>
      )}
      {children}
    </Card>
  )
}

export function ReviewSection({ title, children }) {
  return (
    <div className="fundraiser-wizard__review-section">
      <h4>{title}</h4>
      {children}
    </div>
  )
}

export function ReviewRow({ label, value, children }) {
  return (
    <div className="fundraiser-wizard__review-row">
      <span>{label}</span>
      <span>{children ?? value}</span>
    </div>
  )
}

export function StatusValue({
  enabled,
  enabledLabel = 'Enabled',
  disabledLabel = 'Off',
}) {
  return (
    <>
      <span
        className={`fundraiser-wizard__status-dot ${
          enabled
            ? 'fundraiser-wizard__status-dot--on'
            : 'fundraiser-wizard__status-dot--off'
        }`}
        aria-hidden="true"
      />
      {enabled ? enabledLabel : disabledLabel}
    </>
  )
}

export function FundraiserWizardShell({
  campaignUuid,
  currentStep,
  steps,
  submitMessage,
  submitStatus,
  isSavingDraft,
  isPublishing,
  onStepSelect,
  onBack,
  onNext,
  onSaveDraft,
  onPublish,
  children,
}) {
  const progress = Math.round((currentStep / steps.length) * 100)
  const isLastStep = currentStep === steps.length

  const handleStepClick = (stepNumber) => {
    if (stepNumber <= currentStep + 1) {
      onStepSelect(stepNumber)
    }
  }

  return (
    <div className="fundraiser-wizard">
      <nav className="fundraiser-wizard__sidebar" aria-label="Fundraiser steps">
        <div className="fundraiser-wizard__sidebar-header">
          <h2>{campaignUuid ? 'Edit fundraiser' : 'Create fundraiser'}</h2>
          <p>{campaignUuid ? 'Edit campaign' : 'New campaign'}</p>
        </div>

        <ol className="fundraiser-wizard__step-nav">
          {steps.map((step, index) => {
            const stepNumber = index + 1
            const isActive = stepNumber === currentStep
            const isDone = stepNumber < currentStep

            return (
              <li
                key={step.title}
                className={[isActive ? 'active' : '', isDone ? 'done' : '']
                  .filter(Boolean)
                  .join(' ')}
              >
                <button
                  type="button"
                  className="fundraiser-wizard__step-button"
                  onClick={() => handleStepClick(stepNumber)}
                  disabled={stepNumber > currentStep + 1}
                  aria-current={isActive ? 'step' : undefined}
                >
                  <span
                    className="fundraiser-wizard__step-num"
                    aria-hidden="true"
                  >
                    {isDone ? '✓' : stepNumber}
                  </span>
                  <span className="fundraiser-wizard__step-label">
                    {step.shortLabel || step.title}
                  </span>
                </button>
              </li>
            )
          })}
        </ol>

        <div className="fundraiser-wizard__progress-wrap">
          <ProgressBar now={progress} className="fundraiser-wizard__progress" />
          <p className="fundraiser-wizard__progress-label">
            Step {currentStep} of {steps.length}
          </p>
        </div>
      </nav>

      <div className="fundraiser-wizard__main">
        <div className="fundraiser-wizard__content">
          {submitMessage && (
            <div
              className={`fundraiser-wizard__alert fundraiser-wizard__alert--${submitStatus || 'info'}`}
              role="alert"
            >
              {submitMessage}
            </div>
          )}
          {children}
        </div>

        <div className="fundraiser-wizard__footer">
          <Button
            variant="outline-light"
            size="lg"
            className={`btn-back ${currentStep === 1 ? 'invisible' : ''}`}
            onClick={onBack}
            disabled={currentStep === 1}
          >
            Back
          </Button>

          <div className="fundraiser-wizard__footer-actions">
            {isLastStep && (
              <Button
                variant="outline-light"
                size="lg"
                onClick={onSaveDraft}
                disabled={isSavingDraft || isPublishing}
              >
                {isSavingDraft ? (
                  <>
                    <Spinner
                      as="span"
                      size="sm"
                      animation="border"
                      className="me-2"
                    />
                    Saving...
                  </>
                ) : (
                  'Save draft'
                )}
              </Button>
            )}

            {!isLastStep ? (
              <Button size="lg" className="btn-next" onClick={onNext}>
                Continue
              </Button>
            ) : (
              <Button
                size="lg"
                variant="success"
                onClick={onPublish}
                disabled={isPublishing || isSavingDraft}
              >
                {isPublishing ? (
                  <>
                    <Spinner
                      as="span"
                      size="sm"
                      animation="border"
                      className="me-2"
                    />
                    Publishing...
                  </>
                ) : (
                  'Publish campaign'
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export function CountBadge({ count, label }) {
  return (
    <Badge bg="primary" className="fundraiser-wizard__count-badge">
      {count} {label}
    </Badge>
  )
}
